import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import BirthFields, { birthPayload, emptyBirth } from '../../components/BirthFields/BirthFields';
import { writeSavedChart } from '../../services/savedChart';
import { LANGUAGE_ANSWER_OPTIONS } from '../../data/siteLanguages';
import english from '../../data/chartCopy/en.json';
import {
  EmText, LinkedNote, chartDirection, chartHref, useChartCopy, useChartLanguage,
} from '../../data/chartLanguage';
import { askChart, drawSynastry, sessionPreview } from '../../services/chartService';
import ChartNav from '../BirthChart/ChartNav';
import ChartShare from '../BirthChart/ChartShare';
import { wrapReading } from '../BirthChart/ReadingText';
import {
  SynastryConnections, SynastryDetails, SynastryPeople, SynastryScore, personName,
} from './SynastryView';
import '../BirthChart/BirthChart.css';
import './Synastry.css';

const READING_LANGUAGE_OPTIONS = LANGUAGE_ANSWER_OPTIONS.filter((option) => option.value);

const Synastry = () => {
  const location = useLocation();
  const { language, chooseLanguage } = useChartLanguage();
  const copy = useChartCopy(language);
  const text = copy.synastry;
  const fields = {
    ...copy.fields,
    months: copy.months,
  };
  const [you, setYou] = useState(() => emptyBirth());
  const [them, setThem] = useState(() => emptyBirth());
  const [saved, setSaved] = useState(null);
  const [savedToken, setSavedToken] = useState('');
  const [phase, setPhase] = useState('form');
  const [result, setResult] = useState(null);
  const [reading, setReading] = useState('');
  const [error, setError] = useState('');
  const [readingError, setReadingError] = useState('');
  const [payload, setPayload] = useState(null);
  const [session, setSession] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const abortRef = useRef(null);
  const askAbortRef = useRef(null);
  const topRef = useRef(null);

  useEffect(() => {
    const token = new URLSearchParams(location.search).get('you');
    if (!token) return undefined;
    let live = true;
    sessionPreview(token)
      .then((data) => {
        if (!live) return;
        setSaved(data);
        setSavedToken(token);
      })
      .catch(() => {});
    return () => { live = false; };
  }, [location.search]);

  useEffect(() => () => {
    abortRef.current?.abort();
    askAbortRef.current?.abort();
  }, []);

  const youLabel = personName(result?.people?.you, text.you);
  const themLabel = personName(result?.people?.them, text.them);

  const run = (next) => {
    abortRef.current?.abort();
    askAbortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setError('');
    setReadingError('');
    setReading('');
    setSession(null);
    setAnswers([]);
    setResult(null);
    setPhase('drawing');
    let gotChart = false;
    drawSynastry(next, {
      signal: controller.signal,
      onChart: (data) => {
        gotChart = true;
        setResult(data);
        setPhase('drawn');
        topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      },
      onToken: (token) => setReading((current) => current + token),
      onDone: (data) => {
        setPhase(gotChart ? 'drawn' : 'form');
        if (data?.session) setSession(data.session);
      },
      onError: (message) => {
        if (gotChart) setReadingError(message);
        else {
          setPhase('form');
          setError(message);
        }
      },
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const themPayload = birthPayload(them, { who: them.name.trim() || text.theirDetails, messages: copy.fields });
    if (themPayload.error) {
      setError(themPayload.error);
      return;
    }
    const next = { them: themPayload, language };
    if (saved && savedToken) {
      next.you_session = savedToken;
    } else {
      const youPayload = birthPayload(you, { who: you.name.trim() || text.yourDetails, messages: copy.fields });
      if (youPayload.error) {
        setError(youPayload.error);
        return;
      }
      next.you = youPayload;
      writeSavedChart(youPayload);
    }
    setPayload(next);
    run(next);
  };

  const handleAsk = (event) => {
    event.preventDefault();
    const text = question.trim();
    if (!session || asking || !text) return;
    const controller = new AbortController();
    askAbortRef.current = controller;
    const index = answers.length;
    const update = (change) => setAnswers((current) => current.map((item, itemIndex) => (
      itemIndex === index ? { ...item, ...change(item) } : item
    )));
    setAnswers((current) => [...current, { question: text, answer: '', error: '' }]);
    setQuestion('');
    setAsking(true);
    askChart(session, text, {
      signal: controller.signal,
      onToken: (token) => update((item) => ({ answer: item.answer + token })),
      onDone: () => setAsking(false),
      onError: (message) => {
        update(() => ({ error: message }));
        setAsking(false);
      },
    });
  };

  const handleEdit = () => {
    abortRef.current?.abort();
    askAbortRef.current?.abort();
    setPhase('form');
    setResult(null);
    setReading('');
    setSession(null);
    setAnswers([]);
  };

  const navItems = [
    { id: 'syn-score', label: text.navScore },
    { id: 'syn-reading', label: text.navReading },
    { id: 'syn-connections', label: text.navConnections },
    ...(session ? [{ id: 'syn-ask', label: text.navAsk }] : []),
    { id: 'syn-share', label: text.navShare },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebApplication',
        name: 'Birth Chart Compatibility Calculator',
        url: 'https://nowtarot.com/birth-chart-compatibility/',
        applicationCategory: 'LifestyleApplication',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        description: 'Free synastry: compare two birth charts and get a full compatibility reading.',
      },
      {
        '@type': 'FAQPage',
        mainEntity: english.synastry.faq.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  };

  return (
    <div className="birth-page">
      <Helmet>
        <title>Birth Chart Compatibility: Free Synastry Reading | NowTarot</title>
        <meta name="description" content="Compare two birth charts for free. See your relationship score, the planet connections between you, and a full synastry reading you can ask questions about." />
        <link rel="canonical" href="https://nowtarot.com/birth-chart-compatibility/" />
        <meta property="og:title" content="Birth Chart Compatibility: Free Synastry Reading" />
        <meta property="og:description" content="Compare two birth charts and read how you fit together." />
        <meta property="og:url" content="https://nowtarot.com/birth-chart-compatibility/" />
        <meta property="og:image" content="https://nowtarot.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Header />
      <main className="birth-wide" {...chartDirection(language)}>
        <div className="birth-tool" ref={topRef}>
          {phase === 'form' ? (
            <>
              <header className="birth-intro">
                <p className="birth-eyebrow">{text.eyebrow}</p>
                <h1><EmText text={text.title} /></h1>
                <p className="birth-lede">{text.lede}</p>
              </header>
              <form className="syn-form" onSubmit={handleSubmit}>
                <div className="syn-columns">
                  <section className="syn-column">
                    <h2>{text.you}</h2>
                    {saved ? (
                      <div className="syn-saved">
                        <p>
                          <strong>{saved.name || text.yourChart}</strong>
                          <span>{saved.summary}</span>
                        </p>
                        <button type="button" className="birth-text" onClick={() => { setSaved(null); setSavedToken(''); }}>
                          {text.useDifferent}
                        </button>
                      </div>
                    ) : (
                      <BirthFields value={you} onChange={(next) => { setYou(next); setError(''); }} showName nameLabel={text.yourName} labels={fields} />
                    )}
                  </section>
                  <section className="syn-column">
                    <h2>{text.them}</h2>
                    <BirthFields value={them} onChange={(next) => { setThem(next); setError(''); }} showName nameLabel={text.theirName} labels={fields} />
                  </section>
                </div>
                <div className="syn-submit">
                  <label className="birth-language">
                    {text.readingIn}
                    <select value={language} onChange={(event) => chooseLanguage(event.target.value)} aria-label={text.readingLanguage}>
                      {READING_LANGUAGE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value} lang={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  {error && <p className="birth-error">{error}</p>}
                  <button className="birth-draw" type="submit">{text.button}</button>
                </div>
              </form>
            </>
          ) : (
            <div className="birth-result">
              <div className="birth-summary-row">
                <p className="birth-summary">{result?.summary || text.drawing}</p>
                <button type="button" className="birth-text" onClick={handleEdit}>{text.edit}</button>
              </div>
              {!result && <p className="birth-wait">{text.comparing}</p>}
              {result && (
                <>
                  <ChartNav items={navItems} />
                  {result.time_unknown && (
                    <p className="birth-note">
                      {text.timeUnknown}
                    </p>
                  )}
                  <section id="syn-score" className="birth-section" aria-labelledby="syn-score-title">
                    <h2 id="syn-score-title">{youLabel} and {themLabel}</h2>
                    <SynastryScore score={result.score} />
                    <SynastryPeople people={result.people} youLabel={youLabel} themLabel={themLabel} />
                  </section>
                  <section id="syn-reading" className="birth-section" aria-labelledby="syn-reading-title">
                    <h2 id="syn-reading-title">{text.chartsReading}</h2>
                    <div className="birth-read">
                      <div className="birth-figure syn-figure">
                        <div className="birth-wheel" dangerouslySetInnerHTML={{ __html: result.svg }} />
                        <SynastryDetails people={result.people} overlays={result.overlays} youLabel={youLabel} themLabel={themLabel} />
                      </div>
                      {reading && wrapReading(reading, language)}
                      {!reading && !readingError && <p className="birth-wait">{text.readingCharts}</p>}
                    </div>
                    {readingError && (
                      <p className="birth-error">
                        {readingError}{' '}
                        <button type="button" className="birth-text" onClick={() => payload && run(payload)}>{text.tryReading}</button>
                      </p>
                    )}
                  </section>
                  <section id="syn-connections" className="birth-section" aria-labelledby="syn-connections-title">
                    <h2 id="syn-connections-title">{text.connections}</h2>
                    <SynastryConnections connections={result.connections} youLabel={youLabel} themLabel={themLabel} />
                  </section>
                  {session && (
                    <section id="syn-ask" className="birth-ask birth-section" aria-labelledby="syn-ask-title">
                      <h3 id="syn-ask-title">{text.askTitle}</h3>
                      <p className="birth-note">
                        {text.askNote}
                      </p>
                      {answers.map((item, index) => (
                        <div className="birth-qa" key={index}>
                          <p className="birth-qa-question">{item.question}</p>
                          {item.answer && wrapReading(item.answer, language)}
                          {!item.answer && !item.error && <p className="birth-wait">{text.looking}</p>}
                          {item.error && <p className="birth-error">{item.error}</p>}
                        </div>
                      ))}
                      <form className="birth-ask-form" onSubmit={handleAsk}>
                        <textarea
                          value={question}
                          rows={3}
                          maxLength={500}
                          aria-label={text.askLabel}
                          placeholder={text.askPlaceholder}
                          onChange={(event) => setQuestion(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' && !event.shiftKey) handleAsk(event);
                          }}
                        />
                        <button className="birth-draw" type="submit" disabled={asking || !question.trim()}>
                          {asking ? text.answering : text.ask}
                        </button>
                      </form>
                    </section>
                  )}
                  <section id="syn-share" className="birth-section" aria-labelledby="syn-share-title">
                    <h2 id="syn-share-title">{text.shareTitle}</h2>
                    <ChartShare
                      kind="synastry"
                      session={reading && !readingError ? session : null}
                      hasAnswers={answers.some((item) => item.answer)}
                    />
                  </section>
                  {phase === 'drawn' && (
                    <button type="button" className="birth-again" onClick={handleEdit}>{text.compareElse}</button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <section className="birth-section syn-about">
          <article className="birth-box">
            <h2>{text.aboutTitle}</h2>
            <p>{text.about}</p>
            {text.pairs && (
              <dl className="syn-pairs">
                {text.pairs.map((pair) => (
                  <div key={pair.label}>
                    <dt>{pair.label}</dt>
                    <dd>{pair.text}</dd>
                  </div>
                ))}
              </dl>
            )}
            {text.revealClose && <p>{text.revealClose}</p>}
          </article>
          {text.askExamplesTitle && (
            <div className="birth-box syn-ask-examples">
              <h2>{text.askExamplesTitle}</h2>
              <ul>
                {(text.askExamples || []).map((question) => (
                  <li key={question}>{question}</li>
                ))}
              </ul>
              <p className="birth-note">
                <LinkedNote
                  template={text.signsNote}
                  links={{
                    zodiac: { href: '/zodiac-compatibility/', label: text.zodiacLink },
                    chart: { href: chartHref('/birth-chart/', language), label: text.chartLink },
                  }}
                />
              </p>
            </div>
          )}
          <div className="syn-faq">
            {text.faq.map((item) => (
              <article key={item.q}>
                <h3>{item.q}</h3>
                <p>{item.a}</p>
              </article>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Synastry;
